import { motion } from "framer-motion";
import { Smartphone, Laptop, Tablet, ArrowUpRight, ShoppingBag, MessageCircle } from "lucide-react";
import { waLink } from "@/lib/siteConfig";

const CATEGORIES = [
  {
    id: "phones",
    icon: Smartphone,
    title: "LCD ONLY MacBook Pro",
    image: "/brand/macbook-pro-air.png",
    items: [
      'MacBook Pro 13" 2018 — A1989 Rp2.600.000',
      'MacBook Pro 13" 2019 (Two Ports) A2159 — Rp2.600.000',
      'MacBook Pro 13" 2019 (Four Ports) A1989 — Rp2.600.000',
      'MacBook Pro 13" 2020 Intel (Two Ports) A2289 — Rp2.600.000',
      'MacBook Pro 13" 2020 Intel (Four Ports) A2251 — Rp2.600.000',
      'MacBook Pro 13" M1 2020 A2338 — Rp3.500.000',
      'MacBook Pro 13" M2 2022 A2338 — Rp3.500.000',
      'Belum termasuk pemasangan',
    ],
    accent: "#FF3333",
  },
  {
    id: "laptops",
    icon: Laptop,
    title: "LCD ONLY MacBook Air",
    image: "/brand/macbook-pro-air.png",
    items: [
      'MacBook Air Retina 13" 2018 A1932 — Rp2.600.000',
      'MacBook Air Retina 13" 2019 A1932 — Rp2.600.000',
      'MacBook Air Retina 13" 2020 Intel A2179 — Rp2.600.000',
      'MacBook Air 13" M1 2020 A2337 — Rp2.700.000',
      'MacBook Air 13" M2 2022 A2681 — Rp3.100.000',
      'MacBook Air 13" M3 2024 A3113 — Rp3.100.000',
      'Belum termasuk pemasangan',
    ],
    accent: "#FF6B00",
  },
  {
    id: "tablets",
    icon: Tablet,
    title: "Magic Keyboard for iPad",
    image: "/brand/magic-keyboard.jpeg",
    items: [
      'iPad 10th Gen 10.9" — Rp750.000',
      'iPad 11th Gen 11" — Rp750.000',
      'iPad Air Gen 4 11" — Rp850.000',
      'iPad Air Gen 5 11" — Rp850.000',
      'iPad Air M2 11" — Rp850.000',
      'iPad Air M4 11" — Rp850.000',
      'iPad Pro 11" 2018 — Rp850.000',
      'iPad Pro 11" 2020 — Rp850.000',
      'iPad Pro 11" 2021 — Rp850.000',
      'iPad Pro 11" 2022 — Rp850.000',
      'iPad Pro 11" M4 — Rp950.000',
      'iPad Pro 11" M5 — Rp950.000',
    ],
    accent: "#FFD600",
  },
  {
    id: "lcd-fullset",
    icon: Laptop,
    title: "LCD Macbook Fullset",
    image: "/brand/lcd-macbook-fullset.png",
    items: [
      'Macbook Pro 2017-2018 A1708/A1706 Gray — Rp2.600.000',
      'Belum termasuk pemasangan',
    ],
    accent: "#0A0A0A",
  },
];

export default function SparePartsSection() {
  return (
    <section id="parts" className="py-20 lg:py-28 bg-[#F8F8F8]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">

        {/* HEADER */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <div>
            <div className="eyebrow mb-4">Spare Parts</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95]">
              Ready Stock
              <br />
              <span className="text-[#FF3333]">Siap Kirim. </span> Seluruh Indonesia
            </h2>
          </div>

          <p className="max-w-md text-[#525252]">
            Packaging aman dengan bubble wrap dan proteksi tambahan untuk menjaga device tetap aman selama pengiriman.
          </p>
        </div>

        {/* CARDS */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {CATEGORIES.map((cat, i) => (
            <motion.div
              key={cat.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="bento-card p-6 flex flex-col group"
            >

              {/* IMAGE - 2X BIGGER */}
              <div className="flex justify-center mb-5">
                <div className="w-60 h-60 md:w-64 md:h-64   rounded-2xl overflow-hidden border border-[#E5E5E5] bg-white flex items-center justify-center shadow-sm group-hover:scale-[1.03] transition-transform duration-300">
                  <img
                    src={cat.image}
                    alt={`${cat.title} EZ Device Jakarta — sparepart Apple bergaransi`}
                    className="w-[88%] h-[88%] object-contain"
                  />
                </div>
              </div>

              {/* TITLE */}
              <h3 className="font-display text-2xl font-bold tracking-tight leading-tight text-center">
                {cat.title}
              </h3>

              {/* LIST */}
              <ul className="space-y-1.5 mt-4">
                {cat.items.map((item) => (
                  <li
                    key={item}
                    className="text-sm text-[#525252] flex gap-2 items-start"
                  >
                    <span className="text-[#FF3333] mt-0.5">›</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              {/* FOOTER - dual CTAs */}
              <div className="pt-4 mt-auto border-t border-[#E5E5E5] flex flex-col gap-2">
                <a
                  href="https://shopee.co.id/ezdevicestore?categoryId=100013&entryPoint=ShopByPDP&itemId=49660379296"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between gap-2 px-4 py-2.5 border border-[#0A0A0A] bg-white hover:bg-[#FF3333] hover:text-white hover:border-[#FF3333] transition-colors group/btn"
                  data-testid={`${cat.id}-buy-shopee`}
                >
                  <span className="font-mono-ez text-[11px] uppercase tracking-widest font-bold flex items-center gap-2">
                    <ShoppingBag className="w-3.5 h-3.5" />
                    Buy via Shopee
                  </span>
                  <ArrowUpRight className="w-4 h-4 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5 transition-transform" />
                </a>
                <a
                  href={waLink(`Hi EZ Device, saya mau order ${cat.title}`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between gap-2 px-4 py-2.5 border border-[#0A0A0A] bg-[#0A0A0A] text-white hover:bg-[#FF6B00] hover:border-[#FF6B00] transition-colors group/btn"
                  data-testid={`${cat.id}-buy-whatsapp`}
                >
                  <span className="font-mono-ez text-[11px] uppercase tracking-widest font-bold flex items-center gap-2">
                    <MessageCircle className="w-3.5 h-3.5" />
                    Buy via WhatsApp
                  </span>
                  <ArrowUpRight className="w-4 h-4 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5 transition-transform" />
                </a>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}